import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchDashboardMt4Overview } from './api/dashboardMt4';

import { useDateFilter } from './context/DateFilterContext';

import { useCellBlink } from './hooks/useCellBlink';

import { useDashboardSocket } from './hooks/useDashboardSocket';

import { useMenuFilterRegistration } from './hooks/useMenuFilterRegistration';

import { useDisabledAccounts } from './hooks/useDisabledAccounts';

import {

  DisabledAccountsBanner,

  DisabledAccountsToggle,

} from './components/DisabledAccountsControls';

import {

  buildLoginNameMap,

  filterLoginRows,

  filterSymbolRows,

} from './utils/accountVisibility';

import {

  ACCOUNT_ACTION_COL,

  accountDisableCell,

  accountEnableCell,

} from './utils/accountTableActions';

import { formatCompact, formatMoney } from './utils/format';

import SortableCardGrid from './components/SortableCardGrid/SortableCardGrid';

import {

  DataTable,

  KpiCard,

} from './DashboardShared';

import './Dashboard.css';



export default function DashboardMt4({ active = true }) {

  const [segment, setSegment] = useState('wh');

  const [showDisabledOnly, setShowDisabledOnly] = useState(false);

  const { period, queryFrom, queryTo, historicalMode } = useDateFilter();

  const { disabledSet, disabledLogins, disableLogin, enableLogin, disabledCount } =

    useDisabledAccounts(segment);



  useEffect(() => {

    setShowDisabledOnly(false);

  }, [segment]);



  const fetchOverview = useCallback(

    ({ signal } = {}) =>

      fetchDashboardMt4Overview({

        segment,

        period: historicalMode ? 'today' : period,

        startDate: queryFrom,

        endDate: queryTo,

        signal,

      }),

    [segment, historicalMode, period, queryFrom, queryTo]

  );



  const { data, wsStatus, error, reconnect } = useDashboardSocket({

      segment,

      period: historicalMode ? 'today' : period,

      startDate: queryFrom,

      endDate: queryTo,

      historicalMode,

      wsPath: '/ws/dashboard/mt4',

      wsIntervalMs: Number(process.env.REACT_APP_DASHBOARD_MT4_WS_INTERVAL_MS) || 500,

      skipRestWhenWsConnected: true,

      restPollMs: Number(process.env.REACT_APP_DASHBOARD_MT4_REST_POLL_MS) || 5_000,

      enabled: active,

      onFetch: fetchOverview,

    });



  const floating = data?.floating;

  const closed = data?.closed;

  const orders = data?.orders;

  const dataSource = data?.data_source || 'mt4_mysql';



  const floatingRowsRaw = floating?.per_login ?? [];

  const closedRowsRaw = closed?.per_login ?? [];

  const symbolSourceRaw = useMemo(

    () =>

      (floating?.per_symbol ?? []).map((r) => ({

        ...r,

        id: `${r.login}-${r.symbol}`,

      })),

    [floating?.per_symbol]

  );



  const visibilityOpts = useMemo(

    () => ({ showDisabledOnly }),

    [showDisabledOnly]

  );



  const floatingRows = useMemo(

    () => filterLoginRows(floatingRowsRaw, disabledSet, visibilityOpts),

    [floatingRowsRaw, disabledSet, visibilityOpts]

  );

  const closedRows = useMemo(

    () => filterLoginRows(closedRowsRaw, disabledSet, visibilityOpts),

    [closedRowsRaw, disabledSet, visibilityOpts]

  );

  const symbolSource = useMemo(

    () => filterSymbolRows(symbolSourceRaw, disabledSet, visibilityOpts),

    [symbolSourceRaw, disabledSet, visibilityOpts]

  );



  const closedTotal = useMemo(

    () => closedRows.reduce((s, r) => s + Number(r.profit || 0), 0),

    [closedRows]

  );



  const loginNameById = useMemo(

    () => buildLoginNameMap(floatingRowsRaw, closedRowsRaw),

    [floatingRowsRaw, closedRowsRaw]

  );



  const disabledTableRows = useMemo(() => {

    if (!showDisabledOnly) return [];

    const floatingByLogin = new Map(floatingRowsRaw.map((r) => [Number(r.login), r]));

    const closedByLogin = new Map(closedRowsRaw.map((r) => [Number(r.login), r]));

    return disabledLogins.map((login) => ({

      id: login,

      login,

      name: loginNameById.get(login) || '—',

      floating_pl: floatingByLogin.get(login)?.profit,

      closed_pl: closedByLogin.get(login)?.profit,

    }));

  }, [showDisabledOnly, disabledLogins, floatingRowsRaw, closedRowsRaw, loginNameById]);



  const handleEnableLogin = useCallback(

    (login) => {

      enableLogin(login);

      if (disabledCount <= 1) setShowDisabledOnly(false);

    },

    [enableLogin, disabledCount]

  );



  const mapLoginAction = showDisabledOnly

    ? (login) => accountEnableCell(login, handleEnableLogin)

    : (login) => accountDisableCell(login, disableLogin);



  const floatingBlink = useCellBlink(floatingRows, {

    rowKey: 'login',

    valueKeys: ['profit'],

  });

  const closedBlink = useCellBlink(closedRows, {

    rowKey: 'login',

    valueKeys: ['profit'],

  });

  const symbolBlink = useCellBlink(symbolSource, {

    rowKey: 'id',

    valueKeys: ['floating_pl', 'closed_pl'],

  });



  const floatingKpiTotal = useMemo(() => {

    if (!floatingRows.length) return floating?.total_floating;

    return floatingRows.reduce((s, r) => s + Number(r.profit || 0), 0);

  }, [floating?.total_floating, floatingRows]);



  const closedPlKpi = useMemo(() => {

    if (showDisabledOnly && closedRows.length) {

      return closedRows.reduce((s, r) => s + Number(r.profit || 0), 0);

    }

    if (!showDisabledOnly && disabledSet.size && closedRowsRaw.length) {

      const visible = filterLoginRows(closedRowsRaw, disabledSet, { showDisabledOnly: false });

      return visible.reduce((s, r) => s + Number(r.profit || 0), 0);

    }

    return closed?.total_closed_pl;

  }, [closed?.total_closed_pl, closedRows, closedRowsRaw, disabledSet, showDisabledOnly]);



  const wsLabel = {

    connected: 'Live MT4 (WebSocket)',

    connecting: 'Connecting…',

    disconnected: 'MT4 REST fallback',

    error: 'MT4 WS error — REST fallback',

    historical: 'MySQL snapshot (past dates)',

    paused: 'Tab inactive',

    no_token: 'Set API token',

  }[wsStatus] || wsStatus;



  const segmentToolbar = useMemo(

    () => (

      <div className="segment-toggle segment-toggle--inline">

        <button

          type="button"

          className={segment === 'wh' ? 'segment-btn segment-btn--active' : 'segment-btn'}

          onClick={() => setSegment('wh')}

        >

          WH

        </button>

        <button

          type="button"

          className={segment === 'stp' ? 'segment-btn segment-btn--active' : 'segment-btn'}

          onClick={() => setSegment('stp')}

        >

          STP

        </button>

      </div>

    ),

    [segment]

  );



  const menuToolbar = useMemo(
    () => (
      <>
        <DisabledAccountsToggle
          showDisabledOnly={showDisabledOnly}
          disabledCount={disabledCount}
          onToggle={() => setShowDisabledOnly((v) => !v)}
        />
        {showDisabledOnly && (
          <DisabledAccountsBanner segment={segment} count={disabledCount} />
        )}
        <span className="status-pill status-pill--source" role="status">
          source: {dataSource}
        </span>
      </>
    ),
    [showDisabledOnly, disabledCount, segment, dataSource]
  );

  useMenuFilterRegistration(active, {
    wsStatus,
    wsLabel,
    onReconnect: reconnect,
    showReconnect: !historicalMode,
    reconnectLabel: 'Reconnect',
    closed,
    error,
    extras: segmentToolbar,
    menuToolbar,
  });



  const kpiItems = useMemo(

    () => [

      {

        id: 'floating',

        node: (

          <KpiCard

            label="Floating P/L"

            value={formatCompact(floatingKpiTotal)}

            numericValue={floatingKpiTotal}

            large

          />

        ),

      },

      {

        id: 'closed-pl',

        node: (

          <KpiCard

            label="Close P/L"

            value={formatCompact(closedPlKpi)}

            numericValue={closedPlKpi}

            icon="dollar"

          />

        ),

      },

      {

        id: 'commission',

        node: (

          <KpiCard

            label="Commission"

            value={formatCompact(closed?.total_commission)}

            numericValue={closed?.total_commission}

          />

        ),

      },

      {

        id: 'swap',

        node: (

          <KpiCard

            label="Swap"

            value={formatCompact(closed?.total_swap)}

            numericValue={closed?.total_swap}

          />

        ),

      },

      {

        id: 'deposit',

        node: (

          <KpiCard

            label="Deposit"

            value={formatCompact(closed?.total_deposit)}

            numericValue={closed?.total_deposit}

          />

        ),

      },

      {

        id: 'withdrawal',

        node: (

          <KpiCard

            label="Withdrawal"

            value={formatCompact(closed?.total_withdrawal)}

            numericValue={closed?.total_withdrawal}

          />

        ),

      },

      {

        id: 'active-trading',

        node: (

          <KpiCard

            label="Active Trading"

            value={formatCompact(orders?.active_trading_count)}

            numericValue={orders?.active_trading_count}

            sub="in period"

          />

        ),

      },

      {

        id: 'open-orders',

        node: (

          <KpiCard

            label="Open Orders"

            value={formatCompact(orders?.open_orders_count)}

            numericValue={orders?.open_orders_count}

            sub="in period"

          />

        ),

      },

    ],

    [floatingKpiTotal, closedPlKpi, closed, orders]

  );



  const tableItems = useMemo(() => {

    if (showDisabledOnly) {

      return [

        {

          id: 'disabled-accounts-table',

          node: (

            <DataTable

              title={`Disabled accounts (${disabledCount})`}

              sortKey="mt4-disabled"

              enableColumnFilters

              columns={[

                { key: 'login', label: 'Login' },

                { key: 'name', label: 'Name' },

                { key: 'floating_pl', label: 'Floating P/L', className: 'num' },

                { key: 'closed_pl', label: 'Closed P/L', className: 'num' },

                ACCOUNT_ACTION_COL,

              ]}

              rows={disabledTableRows.map((r) => ({

                id: r.login,

                login: r.login,

                name: r.name,

                floating_pl: r.floating_pl != null ? formatMoney(r.floating_pl) : '—',

                closed_pl: r.closed_pl != null ? formatMoney(r.closed_pl) : '—',

                _action: mapLoginAction(r.login),

                __numeric: {

                  login: Number(r.login),

                  floating_pl: Number(r.floating_pl ?? 0),

                  closed_pl: Number(r.closed_pl ?? 0),

                },

              }))}

            />

          ),

        },

      ];

    }



    const loginCols = (valueKey, valueLabel, extraCols = []) => [

      { key: 'login', label: 'Login' },

      { key: 'name', label: 'Name' },

      ...extraCols,

      { key: valueKey, label: valueLabel, className: 'num' },

      ACCOUNT_ACTION_COL,

    ];



    return [

      {

        id: 'floating-table',

        node: (

          <DataTable

            title={`Floating (${floatingRows.length})`}

            blinkCells={floatingBlink}

            enableColumnFilters

            columns={loginCols('profit', 'Floating P/L')}

            rows={floatingRows.map((r) => ({

              id: r.login,

              login: r.login,

              name: r.name || '—',

              profit: formatMoney(r.profit),

              _action: mapLoginAction(r.login),

              __numeric: { profit: r.profit, login: r.login },

            }))}

          />

        ),

      },

      {

        id: 'closed-table',

        node: (

          <DataTable

            title="Closed PL"

            blinkCells={closedBlink}

            enableColumnFilters

            columns={loginCols('profit', 'Closed P/L', [

              { key: 'equity', label: 'Equity', className: 'num' },

            ])}

            rows={closedRows.map((r) => ({

              id: r.login,

              login: r.login,

              name: r.name || '—',

              profit: formatMoney(r.profit),

              equity: r.equity != null ? formatMoney(r.equity) : '—',

              _action: mapLoginAction(r.login),

              __numeric: { profit: r.profit, equity: r.equity, login: r.login },

            }))}

            footer={

              <tr className="total-row">

                <td colSpan={3}>Total</td>

                <td className="num">{formatMoney(closedTotal)}</td>

                <td />

              </tr>

            }

          />

        ),

      },

      {

        id: 'symbol-table',

        node: (

          <DataTable

            title="Floating and Closed P/L"

            blinkCells={symbolBlink}

            enableColumnFilters

            columns={[

              { key: 'login', label: 'Login' },

              { key: 'symbol', label: 'Symbol' },

              { key: 'floating_pl', label: 'Floating P/L', className: 'num' },

              { key: 'closed_pl', label: 'Closed P/L', className: 'num' },

            ]}

            rows={symbolSource.map((r) => ({

              id: r.id,

              login: r.login,

              symbol: r.symbol,

              floating_pl: formatMoney(r.floating_pl),

              closed_pl: formatMoney(r.closed_pl),

              __numeric: { floating_pl: r.floating_pl, closed_pl: r.closed_pl, login: r.login },

            }))}

          />

        ),

      },

    ];

  }, [

    showDisabledOnly,

    disabledCount,

    disabledTableRows,

    mapLoginAction,

    floatingRows,

    closedRows,

    symbolSource,

    floatingBlink,

    closedBlink,

    symbolBlink,

    closedTotal,

  ]);



  return (

    <div

      className={`godo-dashboard godo-dashboard--mt4${

        historicalMode ? ' godo-dashboard--historical' : ''

      }`}

    >

      <SortableCardGrid storageKey="mt4-kpi-order" className="kpi-grid" section="kpi" items={kpiItems} />

      <SortableCardGrid storageKey="mt4-tables-order" className="tables-row" section="tables" items={tableItems} />

    </div>

  );

}

